import { DeclarationDocumentsEnum, declarationDocumentsIdsMapping, DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { hideElementFunction } from '@components';

export enum FormDocumentsInputType {
  NUMBER = 'NUMBER',
  INPUT = 'INPUT',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  DATE = 'DATE',
  MULTILANG = 'MULTILANG',
}

export enum FormDocumentsDeclarationType {
  ALL = 'ALL',
  HIGH_VALUES = 'HIGH_VALUES',
  LOW_VALUES = 'LOW_VALUES',
}

const PLACEHOLDER = {
  DATE_OF_VALIDITY: 'Date of validity',
  REFERENCE_NUMBER: 'Reference Number',
  TYPE: 'Type',
  CODE: 'Code',
  AMOUNT: 'Amount',
  CURRENCY: 'Currency',
  QUANTITY: 'Quantity',
  MEASUREMENT_UNIT: 'Measurement unit and qualifier',
};

export type DeclarationDocumentSchema = Record<
  string,
  {
    id?: string[];
    declarationType?: FormDocumentsDeclarationType;
    fields: Record<
      string,
      {
        declarationType?: FormDocumentsDeclarationType;
        type: FormDocumentsInputType;
        search?: (search: string, lang: string, isHeader?: boolean) => Promise<{ value: string; label: string }[]>;
        code?: string | string[];
        system?: string | string[];
        placeholder: string;
        'data-pw'?: string;
        show?: (country?: string) => boolean;
        isAmendmentDisabled?: boolean;
        amendmentDisabledCountries?: EnvironmentCountries[];
      }
    >;
  }
>;

export const buildDeclarationDocumentSchema = (country: string, declarationGroup = null, isGoodsItem = false): DeclarationDocumentSchema => ({
  [DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE],
    fields: {
      role: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL149',
        placeholder: 'Role',
        isAmendmentDisabled: true,
        amendmentDisabledCountries: [EnvironmentCountries.BE],
      },
      vatIdentificationNumber: {
        type: FormDocumentsInputType.INPUT,
        'data-pw': 'vat-identification-number-input',
        placeholder: 'Vat identification number',
        isAmendmentDisabled: true,
        amendmentDisabledCountries: [EnvironmentCountries.BE],
      },
      dateOfValidity: {
        type: FormDocumentsInputType.DATE,
        placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
        show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: null }),
        isAmendmentDisabled: true,
        amendmentDisabledCountries: [EnvironmentCountries.BE],
      },
    },
  },
  [DeclarationDocumentsEnum.TRANSPORT_DOCUMENTS]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.TRANSPORT_DOCUMENTS],
    fields: {
      type: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL754',
        placeholder: PLACEHOLDER.TYPE,
      },
      referenceNumber: {
        type: FormDocumentsInputType.INPUT,
        placeholder: PLACEHOLDER.REFERENCE_NUMBER,
      },
      dateOfValidity: {
        type: FormDocumentsInputType.DATE,
        placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
        show: () => !isGoodsItem && !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
    },
  },
  [DeclarationDocumentsEnum.AUTHORISATION_DOCUMENTS]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.AUTHORISATION_DOCUMENTS],
    declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
    fields: {
      type: {
        type: FormDocumentsInputType.MULTILANG,
        code: ['CL605', 'CL615'],
        placeholder: PLACEHOLDER.TYPE,
      },
      referenceNumber: {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Reference',
      },
      holderOfTheAuthorisation: {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Holder of the authorisation',
      },
    },
  },
  [DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS],
    declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
    fields: {
      code: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL791',
        placeholder: PLACEHOLDER.CODE,
      },
      amount: {
        type: FormDocumentsInputType.NUMBER,
        placeholder: PLACEHOLDER.AMOUNT,
      },
    },
  },
  [DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS],
    fields: {
      type: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL214',
        placeholder: PLACEHOLDER.TYPE,
        'data-pw': 'type-input',
      },
      referenceNumber: {
        type: FormDocumentsInputType.INPUT,
        placeholder: PLACEHOLDER.REFERENCE_NUMBER,
        'data-pw': 'reference-number-input',
      },
      dateOfValidity: {
        type: FormDocumentsInputType.DATE,
        placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
        'data-pw': 'date-of-validity-input',
        show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: null }),
      },
      category: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Category',
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      location: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Location',
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      article: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Article',
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      billOfLoading: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Bill of loading',
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      item: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Item',
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      typeOfPackages: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL017',
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        placeholder: 'Type of packages',
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      numberOfPackages: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.NUMBER,
        placeholder: 'Number of packages',
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      measurementUnitAndQualifier: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL349',
        placeholder: PLACEHOLDER.MEASUREMENT_UNIT,
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      quantity: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.NUMBER,
        placeholder: PLACEHOLDER.QUANTITY,
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      goodsItemIdentifier: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Goods item identifier',
      },
    },
  },
  [DeclarationDocumentsEnum.SUPPORTING_DOCUMENTS]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.SUPPORTING_DOCUMENTS],
    fields: {
      type: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL213',
        placeholder: PLACEHOLDER.TYPE,
      },
      referenceNumber: {
        type: FormDocumentsInputType.INPUT,
        placeholder: PLACEHOLDER.REFERENCE_NUMBER,
      },
      dateOfValidity: {
        type: FormDocumentsInputType.DATE,
        placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
        show: () => !hideElementFunction({ systems: ['DECO', 'IDMS_LV'], declarationGroup, country, condition: null }),
      },
      documentLineItemNumber: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.NUMBER,
        placeholder: 'Document line item number',
      },
      issuingAuthorityName: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Issuing authority name',
        show: () =>
          !hideElementFunction({
            declarationGroup,
            country,
            condition: () => declarationGroup === DeclarationHighValueGroupType.H2 && country === EnvironmentCountries.NL,
          }),
      },
      measurementUnitAndQualifier: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL349',
        placeholder: PLACEHOLDER.MEASUREMENT_UNIT,
      },
      quantity: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.NUMBER,
        placeholder: PLACEHOLDER.QUANTITY,
      },
      currency: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL048',
        placeholder: PLACEHOLDER.CURRENCY,
      },
      amount: {
        declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
        type: FormDocumentsInputType.NUMBER,
        placeholder: PLACEHOLDER.AMOUNT,
      },
    },
  },
  [DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS],
    fields: {
      type: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL251',
        placeholder: PLACEHOLDER.TYPE,
      },
      'reference.dateOfValidity': {
        type: FormDocumentsInputType.DATE,
        placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
        show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
      },
      'reference.grn': {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'GRN',
      },
      'reference.accessCode': {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Access Code',
      },
      'reference.currencyCode': {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL048',
        placeholder: PLACEHOLDER.CURRENCY,
      },
      'reference.amountTobeCovered': {
        type: FormDocumentsInputType.NUMBER,
        placeholder: 'Amount to be covered',
      },
      'reference.otherGuaranteeReference': {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Other guarantee reference',
      },
      'reference.customsOfficeGuarantee.referenceNumber': {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL141',
        placeholder: 'Customs Office Reference Number',
      },
    },
  },
  [DeclarationDocumentsEnum.ADDITIONAL_REFERENCE]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONAL_REFERENCE],
    fields: {
      type: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL380',
        placeholder: PLACEHOLDER.TYPE,
      },
      referenceNumber: {
        type: FormDocumentsInputType.INPUT,
        placeholder: PLACEHOLDER.REFERENCE_NUMBER,
        'data-pw': 'reference-number-input',
      },
      dateOfValidity: {
        type: FormDocumentsInputType.DATE,
        placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
        show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: null }),
      },
      producedDocumentsValidationOffice: {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Produced documents validation office',
        show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: null }),
      },
    },
  },
  [DeclarationDocumentsEnum.ADDITIONAL_INFORMATION]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONAL_INFORMATION],
    fields: {
      code: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL239',
        placeholder: PLACEHOLDER.CODE,
      },
      text: {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Text',
      },
      dateOfValidity: {
        type: FormDocumentsInputType.DATE,
        placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
        show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: null }),
      },
    },
  },
  [DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT]: {
    id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT],
    fields: {
      type: {
        type: FormDocumentsInputType.MULTILANG,
        code: 'CL099',
        placeholder: PLACEHOLDER.TYPE,
      },
      identifier: {
        type: FormDocumentsInputType.INPUT,
        placeholder: 'Identifier',
      },
    },
  },
});
